import { useEffect, useState } from "react";
import { UsersRound } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../data/content";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

export default function StaffTeam() {
  const { user } = useAuth();
  const [team, setTeam] = useState([]);
  const [clients, setClients] = useState([]);
  const [roles, setRoles] = useState([]);

  const load = () => {
    api.get("/staff/team").then((r) => setTeam(r.data)).catch(() => {});
    api.get("/staff/clients").then((r) => setClients(r.data)).catch(() => {});
    api.get("/meta/options").then((r) => setRoles(r.data.staff_roles)).catch(() => {});
  };

  useEffect(() => { load(); }, []);

  const setRole = async (target, role) => {
    try {
      await api.patch(`/staff/users/${target.user_id}/role`, { role });
      toast.success(`${target.name} is now ${ROLE_LABELS[role]}.`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update role.");
    }
  };

  if (user?.role !== "administrator") {
    return <p className="text-sm text-mutedink" data-testid="team-admin-only">Only administrators can manage team roles.</p>;
  }

  return (
    <div className="space-y-8" data-testid="staff-team-page">
      <div>
        <h1 className="text-2xl font-serif font-semibold text-navy tracking-tight">Team &amp; Roles</h1>
        <p className="mt-1 text-sm text-mutedink">Role-based access keeps client records visible only to the people who need them. Promote a client account to a staff role here.</p>
      </div>

      <section className="bg-surface border border-line rounded-2xl shadow-sm overflow-hidden" data-testid="team-table">
        <div className="px-6 py-4 border-b border-line flex items-center gap-2">
          <UsersRound className="h-4 w-4 text-teal" />
          <h2 className="text-sm font-sans font-semibold text-navy">Staff ({team.length})</h2>
        </div>
        <div className="divide-y divide-line">
          {team.map((m) => (
            <div key={m.user_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`team-row-${m.user_id}`}>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-navy">{m.name} {m.user_id === user.user_id && <span className="text-xs text-teal">(you)</span>}</p>
                <p className="text-xs text-mutedink">{m.email}</p>
              </div>
              <Select value={m.role} onValueChange={(v) => setRole(m, v)} disabled={m.user_id === user.user_id}>
                <SelectTrigger className="w-64 h-9 text-xs" data-testid={`team-role-${m.user_id}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="client">Client (remove staff access)</SelectItem>
                  {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          ))}
        </div>
      </section>

      <section className="bg-surface border border-line rounded-2xl shadow-sm overflow-hidden" data-testid="promote-table">
        <div className="px-6 py-4 border-b border-line">
          <h2 className="text-sm font-sans font-semibold text-navy">Client accounts ({clients.length})</h2>
          <p className="text-xs text-mutedink mt-1">Promote a client to a staff role. The person must sign in with Google at least once first.</p>
        </div>
        {clients.length === 0 ? (
          <p className="text-sm text-mutedink p-6">No client accounts yet.</p>
        ) : (
          <div className="divide-y divide-line">
            {clients.map((c) => (
              <div key={c.user_id} className="p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid={`promote-row-${c.user_id}`}>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-navy">{c.name}</p>
                  <p className="text-xs text-mutedink">{c.email}</p>
                </div>
                <Select onValueChange={(v) => setRole(c, v)}>
                  <SelectTrigger className="w-64 h-9 text-xs" data-testid={`promote-role-${c.user_id}`}><SelectValue placeholder="Promote to..." /></SelectTrigger>
                  <SelectContent>
                    {roles.map((r) => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}
